<template>
    <v-dialog fullscreen persistent v-model="fileEditorDialog">
        <template v-slot:activator="{ on, attrs }">
            <template v-if="$scopedSlots.activator">
                <slot name="activator" :on="on" :attrs="attrs" :open="openDialog" :disabled="disabled || loading"></slot>
            </template>
            <v-btn v-else-if="isCreateReadmeFile" color="secondary" v-bind="attrs" v-on="on" text>
                <v-icon left>add</v-icon>
                README file
            </v-btn>
            <v-btn
                v-else-if="editable"
                class="justify-start"
                :color="btnColor"
                v-bind="attrs"
                v-on="on"
                :text="!loading"
                :block="isBlock"
                :disabled="disabled"
                :loading="loading">
                <v-icon left small>edit</v-icon>
                edit
            </v-btn>
            <v-btn
                v-else
                class="justify-start"
                :color="btnColor"
                v-bind="attrs"
                v-on="on"
                :text="!loading"
                :block="isBlock"
                :disabled="disabled"
                :loading="loading">
                <v-icon left small>mdi-eye</v-icon>
                view
            </v-btn>
        </template>
        <v-card>
            <v-card-title class="flex-nowrap align-start">
                <div class="d-flex justify-space-between align-center w-100">
                    <div v-if="isCreateReadmeFile" class="d-flex align-center">
                        <span v-if="isCreateReadmeFile">New Readme file</span>
                    </div>
                    <div class="d-flex align-center" v-else>
                        <div class="d-flex flex-column">
                            <span>Edit file</span>
                            <span>{{ [fileData.local_path, fileData.short_id].join('/') }}</span>
                        </div>
                    </div>
                    <div class="d-flex align-center">
                        <template v-if="editable">
                            <v-btn text :disabled="fileLoading" @click="discardChanges">discard</v-btn>
                            <v-btn class="ml-1" :loading="fileLoading" color="primary" text @click="updateFileContent()">save</v-btn>
                        </template>
                        <v-btn icon :disabled="fileLoading" @click="fileEditorDialog = false"><v-icon>clear</v-icon></v-btn>
                    </div>
                </div>
            </v-card-title>

            <v-card-text class="px-0 pb-0">
                <MarkdownEditor
                    editorHeight="calc(100vh - 83px)"
                    @newMarkdownContent="fileContent = $event.value"
                    :editorText="fileContent"
                    :editorMode="originalFileContent.includes('<iframe') ? 'markdown' : 'wysiwyg'"
                    :fetchingFile="fetchingFile"
                    v-if="isMarkdownFile && fileEditorDialog" />
                <MonacoEditor
                    v-else
                    class="monaco-editor"
                    :style="{ height: 'calc(100vh - 82px)', overflow: 'hidden' }"
                    :language="language"
                    v-model="fileContent"
                    :options="{
                        minimap: { enabled: false },
                        fontSize: 14,
                        renderLineHighlight: false,
                        automaticLayout: true,
                        autoIndent: true,
                        lineNumbersMinChars: 5,
                        scrollbar: { vertical: 'visible' },
                        scrollBeyondLastLine: false,
                        smoothScrolling: true,
                        readOnly: !editable
                    }" />
                <div>
                    <v-dialog persistent v-model="changeWarningDialog" max-width="500">
                        <v-card>
                            <v-card-title class="flex-nowrap align-start">
                                <span class="secondary--text">Discard changes</span>
                                <v-spacer></v-spacer>
                                <v-btn @click="changeWarningDialog = false" icon><v-icon>clear</v-icon></v-btn>
                            </v-card-title>

                            <v-card-text>
                                <p>You have unsaved changes.</p>
                                <p>Are you sure you want to discard your changes?</p>
                            </v-card-text>
                            <v-card-actions>
                                <v-spacer></v-spacer>
                                <v-btn color="error" text @click="fileEditorDialog = false">discard</v-btn>
                                <v-btn color="secondary" text @click="changeWarningDialog = false">review changes</v-btn>
                            </v-card-actions>
                        </v-card>
                    </v-dialog>
                </div>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script>
const MonacoEditor = () => import('vue-monaco')
const MarkdownEditor = () => import('@/components/MarkdownEditor')

export default {
    name: 'FileEditor',
    props: {
        fileData: Object,
        endpoint: String,
        language: String,
        isCreateReadmeFile: { default: false, type: Boolean },
        newReadmeFileContent: String,
        isBlock: { default: false, type: Boolean },
        isMarkdownFile: Boolean,
        disabled: { default: false, type: Boolean },
        loading: { default: false, type: Boolean },
        editable: { default: true, type: Boolean }
    },
    components: {
        MonacoEditor,
        MarkdownEditor
    },
    data() {
        return {
            fileContent: '',
            originalFileContent: '',
            fileEditorDialog: false,
            fileLoading: false,
            changeWarningDialog: false,
            addedEventListener: false,
            fetchingFile: false
        }
    },
    computed: {
        btnColor() {
            return this.loading ? '' : 'secondary'
        }
    },
    methods: {
        openDialog() {
            if (this.disabled || this.loading) {
                return
            }
            this.fileEditorDialog = true
        },
        discardChanges() {
            if (this.originalFileContent === this.fileContent) {
                window.removeEventListener('beforeunload', this.warnUnload)
                this.fileEditorDialog = false
            } else {
                this.changeWarningDialog = true
            }
        },
        warnUnload(event) {
            // Cancel the event as stated by the standard.
            event.preventDefault()
            // Older browsers supported custom message
            event.returnValue = ''
        },
        readFileContent() {
            this.fetchingFile = true
            this.$axios
                .get(`/files/${this.fileData.fid}/download`, {
                    timeout: 10000,
                    responseType: 'blob'
                })
                .then(response => {
                    const reader = new FileReader()
                    reader.addEventListener('loadend', e => {
                        const text = e.target.result
                        this.originalFileContent = text
                        this.fileContent = text
                        this.fetchingFile = false
                    })
                    reader.readAsText(response.data)
                })
                .catch(e => {
                    console.log(e)
                    this.fetchingFile = false
                })
        },
        updateFileContent() {
            this.fileLoading = true
            const blob = new Blob([this.fileContent])
            const formData = new FormData()
            if (this.isCreateReadmeFile) {
                formData.append('file', blob, 'README.md')
                formData.append('type', 'text/markdown')
                formData.append('name', 'README.md')
                formData.append('operation', 'CREATE')
            } else {
                formData.append('file', blob, this.fileData.short_id)
                formData.append('type', this.fileData.type)
                formData.append('name', this.fileData.short_id)
                formData.append('operation', 'UPDATE')
            }

            this.$axios
                .post(this.endpoint, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                })
                .then(() => {
                    if (this.isCreateReadmeFile) {
                        this.$emit('readmeFileCreated')
                        this.$store.dispatch('showSnackBar', {
                            snackBarText: 'File saved successfully!',
                            snackBarTimeout: 5000,
                            snackBarIcon: 'check_circle'
                        })
                    } else {
                        this.$emit('readmeFileUpdated')
                        this.$store.dispatch('showSnackBar', {
                            snackBarText: 'File updated successfully!',
                            snackBarTimeout: 5000,
                            snackBarIcon: 'check_circle'
                        })
                    }
                    this.$store.dispatch('snapshotStore/fetchCurrentFiles', { id: this.$route.params.snid, route: this.$route, setFetchingStatus: true })
                })
                .catch(() => {
                    this.$store.dispatch('showSnackBar', {
                        snackBarText: 'Failed to edit the file.',
                        snackBarTimeout: 5000,
                        snackBarIcon: 'error'
                    })
                })
                .finally(() => {
                    window.removeEventListener('beforeunload', this.warnUnload)
                    this.fileLoading = false
                    this.fileEditorDialog = false
                })
        }
    },
    mounted() {
        if (this.disabled) return
        if (!this.isCreateReadmeFile) {
            this.readFileContent()
        } else {
            this.fileContent = this.newReadmeFileContent
            this.originalFileContent = this.newReadmeFileContent
        }
    },
    watch: {
        fileEditorDialog: function (nextVal) {
            if (nextVal && !this.isCreateReadmeFile) {
                this.readFileContent()
            }
        },
        fileContent: {
            handler: function (to, from) {
                if (to !== this.originalFileContent && !this.addedEventListener) {
                    this.addedEventListener = true
                    window.addEventListener('beforeunload', this.warnUnload)
                } else {
                    window.removeEventListener('beforeunload', this.warnUnload)
                }
            },
            immediate: true
        }
    }
}
</script>
